import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Loader2, ShieldCheck, Search } from "lucide-react";

const STEPS = ["Your Business", "Authority & Insurance", "Truck & Trailer"];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/carrier/profile").then((r) => {
      setProfile(r.data);
      setF({ ...r.data, truck_make: r.data.trucks?.[0]?.make, truck_model: r.data.trucks?.[0]?.model,
        trailer_type: r.data.trailers?.[0]?.type, trailer_length: r.data.trailers?.[0]?.length });
      if (r.data.verified) nav("/dashboard");
    });
  }, []);

  const saveStep = async (data, completedStep) => {
    setSaving(true);
    try { await api.put("/carrier/onboarding", { step: STEPS[step], data, completed_step: completedStep }); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const next = async () => {
    const byStep = [
      { legal_name: f.legal_name, dba: f.dba, phone: f.phone, ein: f.ein, business_address: f.business_address, home_market: f.home_market },
      { usdot: f.usdot, mc: f.mc, authority_status: f.authority_status, insurance_provider: f.insurance_provider, insurance_expiration: f.insurance_expiration },
    ];
    await saveStep(byStep[step], step + 1);
    setStep(step + 1);
  };

  const submit = async () => {
    if (!f.agreement_accepted) { toast.error("Please accept the carrier agreement to finish"); return; }
    await saveStep({
      trucks: f.truck_make ? [{ make: f.truck_make, model: f.truck_model }] : (f.trucks || []),
      trailers: f.trailer_type ? [{ type: f.trailer_type, length: parseFloat(f.trailer_length) || 40 }] : (f.trailers || []),
      agreement_accepted: true,
    }, 3);
    try { await api.post("/carrier/submit-for-review"); toast.success("You're all set — XD is verifying your account!"); nav("/dashboard"); }
    catch { toast.error("Submit failed"); }
  };

  if (!profile) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-blue" size={32} /></div></DashboardShell>;

  return (
    <DashboardShell nav={carrierNav}>
      <div className="max-w-2xl">
        <h1 className="font-heading text-3xl font-black tracking-tight">Let's get you set up</h1>
        <p className="mt-2 text-xd-muted">Just 3 quick steps — takes about 2 minutes. You can browse loads anytime; verification unlocks 1-tap booking.</p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-6 mb-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap ${i < step ? "border-xd-green/40 bg-xd-green/10 text-xd-green" : i === step ? "border-xd-blue bg-xd-blue/10 text-xd-blue" : "border-xd-border text-xd-dim"}`}>
                {i < step ? <Check size={13} /> : <span>{i + 1}</span>} {s}
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-xd-border" />}
            </div>
          ))}
        </div>

        <div className="bg-xd-paper border border-xd-border rounded-2xl p-6">
          {step === 0 && (
            <Grid>
              <Field label="Business name" testid="ob-legal-name" value={f.legal_name} onChange={set("legal_name")} placeholder="Reed Hotshot Logistics LLC" />
              <Field label="DBA (optional)" testid="ob-dba" value={f.dba} onChange={set("dba")} />
              <Field label="Phone" testid="ob-phone" value={f.phone} onChange={set("phone")} />
              <Field label="Home market" testid="ob-home" value={f.home_market} onChange={set("home_market")} placeholder="Houston, TX" />
              <Field label="EIN (optional)" testid="ob-ein" value={f.ein} onChange={set("ein")} />
              <div className="sm:col-span-2"><Label className="text-xd-muted text-sm">Business address</Label><Input data-testid="ob-address" value={f.business_address || ""} onChange={set("business_address")} className="mt-1.5 bg-xd-surface border-xd-border h-11 rounded-xl" /></div>
            </Grid>
          )}
          {step === 1 && (
            <Grid>
              <Field label="USDOT #" testid="ob-usdot" value={f.usdot} onChange={set("usdot")} />
              <Field label="MC # (optional)" testid="ob-mc" value={f.mc} onChange={set("mc")} />
              <Field label="Authority status" testid="ob-authority" value={f.authority_status} onChange={set("authority_status")} placeholder="Active" />
              <Field label="Insurance provider" testid="ob-ins-provider" value={f.insurance_provider} onChange={set("insurance_provider")} />
              <Field label="Insurance expiration" type="date" testid="ob-ins-exp" value={f.insurance_expiration} onChange={set("insurance_expiration")} />
              <p className="sm:col-span-2 text-xs text-xd-dim">You can upload your COI and W-9 anytime in your Document Vault — it speeds up verification.</p>
            </Grid>
          )}
          {step === 2 && (
            <>
              <Grid>
                <Field label="Truck make" testid="ob-truck-make" value={f.truck_make} onChange={set("truck_make")} placeholder="Ram" />
                <Field label="Truck model" testid="ob-truck-model" value={f.truck_model} onChange={set("truck_model")} placeholder="3500" />
                <Field label="Trailer type" testid="ob-trailer-type" value={f.trailer_type} onChange={set("trailer_type")} placeholder="Gooseneck" />
                <Field label="Trailer length (ft)" type="number" testid="ob-trailer-len" value={f.trailer_length} onChange={set("trailer_length")} placeholder="40" />
              </Grid>
              <label className="mt-5 flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="ob-agreement" checked={!!f.agreement_accepted} onCheckedChange={(v) => setF({ ...f, agreement_accepted: v })} className="mt-0.5 border-xd-border data-[state=checked]:bg-xd-blue" />
                <span className="text-sm text-xd-muted">I accept the XD FREIGHT Carrier Agreement and confirm my information is accurate.</span>
              </label>
            </>
          )}

          <div className="mt-6 flex justify-between gap-3">
            <Button data-testid="ob-back" variant="ghost" className="text-xd-muted hover:text-white" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="ob-next" onClick={next} disabled={saving} className="bg-xd-blue hover:bg-xd-blueH text-white font-semibold px-8 rounded-xl">{saving ? <Loader2 className="animate-spin" size={16} /> : "Continue"}</Button>
            ) : (
              <Button data-testid="ob-submit" onClick={submit} disabled={saving} className="bg-xd-gold hover:bg-xd-goldH text-xd-bg font-bold px-8 rounded-xl"><ShieldCheck size={16} className="mr-2" /> Finish & Get Verified</Button>
            )}
          </div>
        </div>

        <Button variant="ghost" data-testid="ob-browse-loads" className="mt-4 text-xd-muted hover:text-white" onClick={() => nav("/find-loads")}><Search size={16} className="mr-2" /> Browse loads first</Button>
      </div>
    </DashboardShell>
  );
}

function Grid({ children }) { return <div className="grid gap-4 sm:grid-cols-2">{children}</div>; }
function Field({ label, testid, ...props }) {
  return <div><Label className="text-xd-muted text-sm">{label}</Label><Input data-testid={testid} {...props} value={props.value || ""} className="mt-1.5 bg-xd-surface border-xd-border h-11 rounded-xl" /></div>;
}
